import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  FC,
  ReactNode,
  useMemo,
} from "react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  Theme,
} from "@mui/material/styles";
import baseTheme from "../styles/theme";
import { ThemeContextType } from "../types";

const ThemeContext = createContext<ThemeContextType>({
  mode: "light",
  toggleMode: () => {
    console.log("toggleMode");
  },
});

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<"light" | "dark">("light");

  useEffect(() => {
    const savedMode = localStorage.getItem("themeMode") as
      | "light"
      | "dark"
      | null;
    if (savedMode) {
      setMode(savedMode);
    } else {
      // Check system preference
      const prefersDarkMode = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      setMode(prefersDarkMode ? "dark" : "light");
    }
  }, []);

  const toggleMode = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem("themeMode", newMode);
  };

  const theme: Theme = useMemo(
    () =>
      createTheme({
        ...baseTheme,
        palette: {
          ...baseTheme.palette,
          mode,
          primary: {
            ...(mode === "light"
              ? baseTheme.palette.primary
              : {
                  main: "#60a5fa", // Lighter blue for dark mode
                  dark: "#3b82f6",
                  light: "#93c5fd",
                  contrastText: "#ffffff",
                }),
          },
          secondary: {
            ...(mode === "light"
              ? baseTheme.palette.secondary
              : {
                  main: "#a78bfa", // Lighter purple for dark mode
                  dark: "#8b5cf6",
                  light: "#c4b5fd",
                  contrastText: "#ffffff",
                }),
          },
          success: {
            ...(mode === "light"
              ? baseTheme.palette.success
              : {
                  main: "#10b981", // Adjusted green for dark mode
                  dark: "#059669",
                  light: "#34d399",
                  contrastText: "#ffffff",
                }),
          },
          error: {
            ...(mode === "light"
              ? baseTheme.palette.error
              : {
                  main: "#f87171", // Lighter red for dark mode
                  dark: "#ef4444",
                  light: "#fca5a5",
                  contrastText: "#ffffff",
                }),
          },
          info: {
            ...(mode === "light"
              ? baseTheme.palette.info
              : {
                  main: "#38bdf8", // Brighter blue for dark mode
                  dark: "#0ea5e9",
                  light: "#7dd3fc",
                  contrastText: "#ffffff",
                }),
          },
          background: {
            default: mode === "light" ? "#f5f7fa" : "#121212",
            paper: mode === "light" ? "#ffffff" : "#1e1e1e",
          },
          text: {
            primary:
              mode === "light" ? baseTheme.palette.text.primary : "#e2e8f0",
            secondary:
              mode === "light" ? baseTheme.palette.text.secondary : "#cbd5e1",
            disabled:
              mode === "light" ? baseTheme.palette.text.disabled : "#64748b",
          },
          divider: mode === "light" ? "#e2e8f0" : "#2d3748",
          action: {
            active:
              mode === "light"
                ? "rgba(0, 0, 0, 0.54)"
                : "rgba(255, 255, 255, 0.7)",
            hover:
              mode === "light"
                ? "rgba(0, 0, 0, 0.04)"
                : "rgba(255, 255, 255, 0.08)",
            selected:
              mode === "light"
                ? "rgba(0, 0, 0, 0.08)"
                : "rgba(255, 255, 255, 0.16)",
            disabled:
              mode === "light"
                ? "rgba(0, 0, 0, 0.26)"
                : "rgba(255, 255, 255, 0.3)",
            disabledBackground:
              mode === "light"
                ? "rgba(0, 0, 0, 0.12)"
                : "rgba(255, 255, 255, 0.12)",
          },
        },
        components:
          mode === "dark"
            ? {
                MuiChip: {
                  styleOverrides: {
                    root: {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    outlined: {
                      borderWidth: 1.5,
                    },
                  },
                },
                MuiPaper: {
                  styleOverrides: {
                    root: {
                      backgroundImage: "none",
                    },
                  },
                },
                MuiAppBar: {
                  styleOverrides: {
                    root: {
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                    },
                  },
                },
                MuiInputBase: {
                  styleOverrides: {
                    input: {
                      "&::placeholder": {
                        color: "rgba(255, 255, 255, 0.5)",
                        opacity: 1,
                      },
                    },
                  },
                },
              }
            : {},
      }),
    [mode],
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeMode = (): ThemeContextType => useContext(ThemeContext);
